import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Title3,
} from "@fluentui/react-components";
import { Add24Regular } from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import {
  getDiscussionsBySpreadsheet,
  DiscussionDto,
} from "../../services/discussions.service";
import {
  getStatusItemsBySpreadsheet,
  StatusItemDto,
} from "../../services/status-items.service";
import { getUserFriendlyErrorMessage } from "../../utils/error-messages";
import { getStatusGroup } from "../../utils/discussionStatusHelpers";
import { useCloseOnNavigation } from "../../hooks/useCloseOnNavigation";
import { useFetch } from "../../hooks/useFetch";
import DiscussionStatistics from "./components/DiscussionStatistics";
import DiscussionFilters from "./components/DiscussionFilters";
import DiscussionCard from "./components/DiscussionCard";
import EmptyState from "./components/EmptyState";
import CreateDiscussionDialog from "./components/CreateDiscussionDialog";
import DiscussionThread from "./components/DiscussionThread";

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    height: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXXL,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorPaletteRedForeground1,
  },
  discussionListContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },
  discussionList: {
    display: "flex",
    flexDirection: "column",
  },
});

/**
 * Discussion threads with filters, statistics, and an inline thread view
 * for the selected discussion.
 */
const Discussions: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { spreadsheetId, organizationUsers } = useWorkflowIntelligence();

  // Data
  const {
    data: discussionsData,
    isLoading,
    error,
    refetch: refetchDiscussions,
    setData: setDiscussions,
  } = useFetch(
    () => getDiscussionsBySpreadsheet(spreadsheetId!),
    [spreadsheetId],
    { enabled: !!spreadsheetId, transformError: getUserFriendlyErrorMessage }
  );
  const discussions = discussionsData ?? [];

  const { data: statusItemsData } = useFetch(
    () => getStatusItemsBySpreadsheet(spreadsheetId!),
    [spreadsheetId],
    { enabled: !!spreadsheetId }
  );
  const statusItems = statusItemsData ?? [];

  // State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogInitialType, setDialogInitialType] = useState<string | null>(null);
  const [selectedDiscussion, setSelectedDiscussion] = useState<DiscussionDto | null>(null);

  // Filter state — initialize search and type from URL params for deep linking
  const [search, setSearch] = useState(() => {
    const urlSearch = searchParams.get("search");
    return urlSearch ? decodeURIComponent(urlSearch) : "";
  });
  const [typeFilter, setTypeFilter] = useState(() => {
    const urlType = searchParams.get("type");
    // Only use as filter if not paired with create=true (that's for the dialog)
    return urlType && searchParams.get("create") !== "true" ? urlType : "all";
  });
  const [tagFilter, setTagFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState(() => {
    const urlStatus = searchParams.get("status");
    return urlStatus ? decodeURIComponent(urlStatus) : "all";
  });
  const [sortBy, setSortBy] = useState("newest");

  // Status item filter - initialize from URL params for deep linking support
  const [statusItemFilter, setStatusItemFilter] = useState(() => {
    const statusItemId = searchParams.get("statusItemId");
    return statusItemId || "all";
  });

  // Check for create param to auto-open dialog from keyboard shortcut
  useEffect(() => {
    if (searchParams.get("create") === "true") {
      // Check for initial type param (e.g., review_point)
      const initialType = searchParams.get("type");
      setDialogInitialType(initialType);
      setSelectedDiscussion(null); // Return to list view if viewing a thread
      setIsDialogOpen(true);
      // Clear the params to prevent reopening on refresh
      searchParams.delete("create");
      searchParams.delete("type");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Deep-link to a specific discussion via `?discussionId=<id>`. Resolves once
  // the discussion list has loaded. The `?from=...` param (if present) is kept
  // in the URL so the back button can route back to the originating page.
  useEffect(() => {
    const idParam = searchParams.get("discussionId");
    if (!idParam || discussions.length === 0) return;
    if (selectedDiscussion?.id.toString() === idParam) return;
    const target = discussions.find((d) => d.id.toString() === idParam);
    if (target) setSelectedDiscussion(target);
  }, [searchParams, discussions, selectedDiscussion]);

  useCloseOnNavigation(() => setIsDialogOpen(false));

  // Predicate mirroring the filter branches below. Extracted so
  // handleDiscussionUpdated can ask "does the updated discussion still match
  // the current filters?" and advance the selection when it doesn't —
  // without duplicating filter logic in two places.
  const matchesFilters = useCallback((d: DiscussionDto): boolean => {
    if (search) {
      const lowerSearch = search.toLowerCase();
      if (!(
        d.title.toLowerCase().includes(lowerSearch) ||
        d.description?.toLowerCase().includes(lowerSearch)
      )) return false;
    }
    if (typeFilter !== "all" && d.type !== typeFilter) return false;
    if (tagFilter !== "all" && !(d.tags && d.tags.includes(tagFilter))) return false;
    if (statusItemFilter !== "all") {
      const statusItemIdNum = parseInt(statusItemFilter, 10);
      if (!d.statusItemIds.includes(statusItemIdNum)) return false;
    }
    if (statusFilter !== "all" && getStatusGroup(d.status) !== statusFilter) return false;
    return true;
  }, [search, typeFilter, tagFilter, statusItemFilter, statusFilter]);

  // Apply filters and sorting
  const filteredDiscussions = useMemo(() => {
    const result = discussions.filter(matchesFilters);

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case "oldest":
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case "updated":
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case "type":
          // Sort by type (main type, then first tag alphabetically)
          const typeCompare = a.type.localeCompare(b.type);
          if (typeCompare !== 0) return typeCompare;
          // If main types are the same, sort by first tag
          const aFirstTag = a.tags && a.tags.length > 0 ? a.tags[0] : "";
          const bFirstTag = b.tags && b.tags.length > 0 ? b.tags[0] : "";
          return aFirstTag.localeCompare(bFirstTag);
        default:
          return 0;
      }
    });

    return result;
  }, [discussions, matchesFilters, sortBy]);

  // Calculate statistics
  const statistics = useMemo(() => ({
    openCount: discussions.filter((d) => getStatusGroup(d.status) === "open").length,
    inProgressCount: discussions.filter((d) => getStatusGroup(d.status) === "in_progress").length,
    resolvedCount: discussions.filter((d) => getStatusGroup(d.status) === "closed").length,
  }), [discussions]);

  // Calculate navigation info for discussion thread
  const navigationInfo = useMemo(() => {
    if (!selectedDiscussion) return null;
    const currentIndex = filteredDiscussions.findIndex(d => d.id === selectedDiscussion.id);
    return {
      currentIndex,
      totalCount: filteredDiscussions.length,
      hasPrevious: currentIndex > 0,
      hasNext: currentIndex < filteredDiscussions.length - 1,
    };
  }, [selectedDiscussion, filteredDiscussions]);

  // Handlers
  const handleCreateDiscussion = () => {
    setDialogInitialType(null); // Clear any initial type when opening manually
    setIsDialogOpen(true);
  };

  const handleDialogDismiss = () => {
    setIsDialogOpen(false);
    setDialogInitialType(null);
  };

  const handleDialogSuccess = () => {
    refetchDiscussions();
  };

  const handleDiscussionClick = (discussion: DiscussionDto) => {
    setSelectedDiscussion(discussion);
  };

  const handleBackToList = () => {
    // If we landed here via a deep link from another page (e.g. Review),
    // navigate back to that page instead of showing the discussion list.
    if (searchParams.get("from") === "review") {
      navigate("/review");
      return;
    }
    setSelectedDiscussion(null);
    // Strip deep-link params so refreshing from the list view doesn't reopen
    // the previous thread.
    if (searchParams.has("discussionId") || searchParams.has("from")) {
      searchParams.delete("discussionId");
      searchParams.delete("from");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleNavigatePrevious = () => {
    if (!navigationInfo || !navigationInfo.hasPrevious) return;
    const prevDiscussion = filteredDiscussions[navigationInfo.currentIndex - 1];
    setSelectedDiscussion(prevDiscussion);
  };

  const handleNavigateNext = () => {
    if (!navigationInfo || !navigationInfo.hasNext) return;
    const nextDiscussion = filteredDiscussions[navigationInfo.currentIndex + 1];
    setSelectedDiscussion(nextDiscussion);
  };

  const handleDiscussionUpdated = (updatedDiscussion: DiscussionDto) => {
    // Update the discussion in the list
    setDiscussions((prev) =>
      prev?.map((d) => (d.id === updatedDiscussion.id ? updatedDiscussion : d))
    );

    // If the update keeps the discussion in the filtered list, stay put.
    if (matchesFilters(updatedDiscussion)) {
      setSelectedDiscussion(updatedDiscussion);
      return;
    }

    // Otherwise the discussion is about to drop out of the filter (e.g. the
    // user marked "Resolved" while the "Open" status filter is active).
    // Advance the selection so the user doesn't get stranded with a "0 of Y"
    // header and broken prev/next arrows: pick the neighbour that will shift
    // into the current slot after removal, step back one if we WERE the last
    // item, or exit to the list view if we were the only item.
    const oldIndex = filteredDiscussions.findIndex((d) => d.id === updatedDiscussion.id);
    if (oldIndex === -1) {
      setSelectedDiscussion(updatedDiscussion);
      return;
    }

    const targetIndex = oldIndex + 1 < filteredDiscussions.length
      ? oldIndex + 1
      : oldIndex - 1;

    if (targetIndex >= 0) {
      setSelectedDiscussion(filteredDiscussions[targetIndex]);
    } else {
      setSelectedDiscussion(null);
    }
  };

  const handleDiscussionArchived = (discussionId: number) => {
    // Remove the archived discussion from the list
    setDiscussions((prev) => prev?.filter((d) => d.id !== discussionId));
  };

  const handleClearFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setTagFilter("all");
    setStatusFilter("all");
    setStatusItemFilter("all");
  };

  const hasFilters = search !== "" || typeFilter !== "all" || tagFilter !== "all" || statusFilter !== "all" || statusItemFilter !== "all";

  // Pick the main content based on state. Kept as a variable (not multiple
  // early returns) so the CreateDiscussionDialog below stays mounted across
  // state transitions — otherwise refetchDiscussions() flipping isLoading
  // back to true briefly unmounts the dialog mid-flow, discarding step /
  // selectedMainType / createAnother during "Create another" cycles.
  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner label="Loading discussions..." />
        </div>
      </div>
    );
  } else if (error) {
    content = (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text weight="semibold">Error loading discussions</Text>
          <Text>{error}</Text>
        </div>
      </div>
    );
  } else if (selectedDiscussion) {
    content = (
      <DiscussionThread
        discussion={selectedDiscussion}
        statusItems={statusItems}
        onBack={handleBackToList}
        onDiscussionUpdated={handleDiscussionUpdated}
        onDiscussionArchived={handleDiscussionArchived}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        currentIndex={navigationInfo?.currentIndex}
        totalCount={navigationInfo?.totalCount}
        users={organizationUsers}
      />
    );
  } else {
    content = (
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <div>
              <Title3>Discussions</Title3>
            </div>
            <Button
              appearance="primary"
              icon={<Add24Regular />}
              onClick={handleCreateDiscussion}
            >
              New Discussion
            </Button>
          </div>
        </div>

        {/* Statistics Dashboard */}
        <DiscussionStatistics
          openCount={statistics.openCount}
          inProgressCount={statistics.inProgressCount}
          resolvedCount={statistics.resolvedCount}
          onStatusClick={setStatusFilter}
        />

        {/* Filters */}
        <DiscussionFilters
          search={search}
          onSearchChange={setSearch}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          tagFilter={tagFilter}
          onTagFilterChange={setTagFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          statusItemFilter={statusItemFilter}
          onStatusItemFilterChange={setStatusItemFilter}
          statusItems={statusItems}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          resultCount={filteredDiscussions.length}
          onClearFilters={handleClearFilters}
        />

        {/* Discussion List or Empty State */}
        <div className={styles.discussionListContainer}>
          {filteredDiscussions.length === 0 ? (
            <EmptyState
              hasFilters={hasFilters}
              onCreateClick={!hasFilters ? handleCreateDiscussion : undefined}
            />
          ) : (
            <div className={styles.discussionList}>
              {filteredDiscussions.map((discussion) => (
                <DiscussionCard
                  key={discussion.id}
                  discussion={discussion}
                  onClick={handleDiscussionClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {content}
      {/* Create Discussion Dialog — always mounted so its internal state (step,
          selected type, "Create another" toggle) survives the refetch triggered
          by a successful submission. Fluent Dialog is a portal, so its position
          in the tree doesn't affect layout. */}
      {spreadsheetId && (
        <CreateDiscussionDialog
          isOpen={isDialogOpen}
          onDismiss={handleDialogDismiss}
          spreadsheetId={spreadsheetId}
          users={organizationUsers}
          onSuccess={handleDialogSuccess}
          initialType={dialogInitialType}
        />
      )}
    </>
  );
};

export default Discussions;
